<script setup lang="ts">
/**
 * Busca pública de produtos monitorados — cada produto mostra o melhor
 * preço atual entre as lojas e leva à página com histórico completo.
 */
interface ProductOffer {
  id: string
  lastPrice: number | null
  inStock: boolean
  store: { id: string, name: string, slug: string, logoUrl: string | null }
}

interface ProductListItem {
  id: string
  slug: string
  title: string
  brand: string | null
  imageUrl: string | null
  category: { id: string, name: string, slug: string } | null
  offers: ProductOffer[]
}

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()

const search = ref((route.query.q as string) ?? '')
const page = ref(1)

const { data, status } = await useFetch<{ items: ProductListItem[], total: number, totalPages: number, hasMore: boolean }>('/products', {
  baseURL: config.public.apiBaseUrl,
  key: 'public-products',
  query: computed(() => ({ q: search.value || undefined, page: page.value, limit: 24 })),
  watch: [page],
})

const products = computed(() => data.value?.items ?? [])

let searchTimeout: ReturnType<typeof setTimeout> | undefined
watch(search, (value) => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    page.value = 1
    router.replace({ query: value ? { q: value } : {} })
  }, 400)
})

function bestPrice(product: ProductListItem): { price: number, store: string } | null {
  const offers = product.offers
    .filter(o => o.inStock && o.lastPrice !== null)
    .sort((a, b) => (a.lastPrice ?? Infinity) - (b.lastPrice ?? Infinity))
  const best = offers[0]
  return best ? { price: best.lastPrice!, store: best.store.name } : null
}

function formatMoney(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

useSeoMeta({
  title: 'Monitor de Preços — Compare e veja o histórico | Descontito',
  description: 'Acompanhe o preço de produtos em Amazon, Mercado Livre, Shopee, Kabum e Magalu. Histórico real para saber se a promoção vale a pena.',
})
</script>

<template>
  <div class="max-w-6xl mx-auto px-4 py-6">
    <div class="mb-6">
      <h1 class="text-xl font-bold text-gray-900 dark:text-white">Monitor de Preços</h1>
      <p class="text-sm text-gray-500 mt-1">
        Produtos acompanhados pelos nossos robôs em múltiplas lojas — veja o histórico antes de comprar.
      </p>
    </div>

    <UInput
      v-model="search"
      icon="i-heroicons-magnifying-glass"
      placeholder="Buscar produto monitorado..."
      size="lg"
      class="w-full max-w-xl mb-6"
    />

    <div
      v-if="products.length > 0"
      class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
    >
      <NuxtLink
        v-for="product in products"
        :key="product.id"
        :to="`/produto/${product.slug}`"
        class="group flex flex-col rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden hover:shadow-md transition-shadow"
      >
        <div class="aspect-square bg-white flex items-center justify-center overflow-hidden border-b border-gray-100 dark:border-gray-800">
          <NuxtImg
            v-if="product.imageUrl"
            :src="product.imageUrl"
            :alt="product.title"
            class="object-contain w-full h-full p-3 group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            decoding="async"
          />
          <UIcon v-else name="i-heroicons-cube" class="w-10 h-10 text-gray-300" />
        </div>

        <div class="p-3 flex-1 flex flex-col gap-1.5">
          <p class="text-xs text-gray-400">{{ product.brand || product.category?.name || ' ' }}</p>
          <h2 class="text-sm font-medium text-gray-900 dark:text-white line-clamp-2 group-hover:text-primary-500 transition-colors">
            {{ product.title }}
          </h2>

          <div class="mt-auto pt-2">
            <template v-if="bestPrice(product)">
              <p class="text-lg font-black text-primary-500">{{ formatMoney(bestPrice(product)!.price) }}</p>
              <p class="text-xs text-gray-400">
                melhor preço · {{ bestPrice(product)!.store }} · {{ product.offers.length }} loja(s)
              </p>
            </template>
            <p v-else class="text-sm text-gray-400">Sem oferta disponível</p>
          </div>
        </div>
      </NuxtLink>
    </div>

    <div v-else-if="status !== 'pending'" class="text-center py-16">
      <UIcon name="i-heroicons-chart-bar-square" class="w-12 h-12 text-gray-300 mx-auto mb-3" />
      <p class="text-sm text-gray-500">Nenhum produto monitorado encontrado.</p>
    </div>

    <div v-if="(data?.totalPages ?? 1) > 1" class="flex justify-center mt-8">
      <UPagination v-model:page="page" :total="data?.total ?? 0" :items-per-page="24" />
    </div>
  </div>
</template>
